import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  CampaignRounded,
  PeopleRounded,
  RocketLaunchRounded,
  BoltRounded,
  CheckCircleRounded,
  CloseRounded,
  PlayArrowRounded,
  PsychologyRounded,
  StorageRounded,
  AnalyticsRounded,
  LightbulbRounded,
  FormatQuoteRounded,
  ArrowForwardRounded,
  KeyboardArrowDownRounded,
  AttachMoneyRounded,
  TrendingUpRounded,
  SettingsRounded,
  ShieldRounded,
  ReceiptLongRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";

const alpha = (color, opacity) => {
  const hex = color.startsWith("#") ? color : `#${color}`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// ─── DATA ──────────────────────────────────────────────────────────────────────
const aiManagers = [
  {
    id: "growth-ai",
    name: "Growth AI",
    title: "Marketing Visibility",
    icon: <CampaignRounded />,
    accent: "#7c5ce6",
    description:
      "Identifies where prospects drop off, evaluates lead quality, and measures pipeline health.",
    detailedDescription:
      "Growth AI continuously analyzes your marketing channels, lead sources, and conversion paths. It spots bottlenecks in your prospect journey, evaluates lead quality to prioritize high-value opportunities, and provides a real-time health score for your entire pipeline.",
    features: [
      "Lead Source Analytics",
      "Pipeline Health Scoring",
      "Drop-off Identification",
      "ROI Forecasting",
    ],
    value: "Increase marketing ROI by 35%",
    efficiency: 92,
  },
  {
    id: "finance-ai",
    name: "Finance AI",
    title: "Financial Structure",
    icon: <AttachMoneyRounded />,
    accent: "#472ed1",
    description:
      "Tracks margin pressure, revenue stability, and profitability risks.",
    detailedDescription:
      "Finance AI monitors your firm's financial health in real time. It alerts you to margin erosion, flags revenue instability, and predicts cash flow gaps before they become crises.",
    features: [
      "Margin Analysis",
      "Revenue Forecasting",
      "Cash Flow Alerts",
      "Profitability Insights",
    ],
    value: "Improve profitability by 25–35%",
    efficiency: 88,
  },
  {
    id: "ops-ai",
    name: "Ops AI",
    title: "Workflow & Delegation",
    icon: <SettingsRounded />,
    accent: "#6247cc",
    description:
      "Highlights bottlenecks in processes and identifies delegation gaps.",
    detailedDescription:
      "Ops AI maps your entire workflow from intake to case closure and pinpoints where work gets stuck or delayed. It identifies tasks that should be delegated, automated, or eliminated.",
    features: [
      "Bottleneck Detection",
      "Delegation Analysis",
      "Process Optimization",
      "Efficiency Scoring",
    ],
    value: "Reduce operational costs by 30%",
    efficiency: 95,
  },
  {
    id: "sales-ai",
    name: "Sales AI",
    title: "Conversion & Pipeline",
    icon: <TrendingUpRounded />,
    accent: "#8f6ef0",
    description:
      "Analyzes funnel conversion, points of leakage, and client onboarding readiness.",
    detailedDescription:
      "Sales AI tracks every lead from first contact to signed client. It reveals where prospects drop out of your funnel and how quickly you're moving leads through the pipeline.",
    features: [
      "Funnel Analytics",
      "Conversion Tracking",
      "Lead Scoring",
      "Onboarding Optimization",
    ],
    value: "Increase conversion rates by 35%",
    efficiency: 85,
  },
  {
    id: "hr-ai",
    name: "HR AI",
    title: "Performance & Hiring",
    icon: <PeopleRounded />,
    accent: "#5941d8",
    description:
      "Tells you when hiring will actually create leverage—not chaos.",
    detailedDescription:
      "HR AI analyzes team workload, performance metrics, and growth trajectory to determine the optimal timing for new hires. Ensures you add team members only when they'll increase leverage.",
    features: [
      "Hiring Readiness Score",
      "Workload Analysis",
      "Skill Gap Identification",
      "Retention Risk Alerts",
    ],
    value: "Reduce hiring time by 60%",
    efficiency: 87,
  },
  {
    id: "compliance-ai",
    name: "Compliance AI",
    title: "Risk & Standards",
    icon: <ShieldRounded />,
    accent: "#3a25b0",
    description: "Monitors compliance indicators and risk exposure trends.",
    detailedDescription:
      "Compliance AI continuously scans your firm's activities for potential ethical and regulatory risks. Tracks conflict checks, engagement letter compliance, deadline adherence, and data privacy standards.",
    features: [
      "Regulatory Monitoring",
      "Conflict Check Automation",
      "Deadline Compliance",
      "Risk Scoring",
    ],
    value: "Reduce compliance costs by 40%",
    efficiency: 94,
  },
  {
    id: "document-ai",
    name: "Document AI",
    title: "Management & Accuracy",
    icon: <ReceiptLongRounded />,
    accent: "#7059df",
    description:
      "Organizes, prioritizes, and tracks document workflows for consistency.",
    detailedDescription:
      "Document AI automates the entire document lifecycle from creation to storage. Generates templates, reviews for accuracy, manages version control, and ensures all filings meet court standards.",
    features: [
      "Template Automation",
      "Version Control",
      "Accuracy Checks",
      "Workflow Tracking",
    ],
    value: "Reduce document prep time by 80%",
    efficiency: 91,
  },
];

const testimonials = [
  {
    quote:
      "LexMom's AI identified bottlenecks we didn't even know existed. Within 3 months, our case throughput increased by 28%.",
    author: "Sarah Chen",
    title: "Managing Partner, Chen & Associates",
  },
  {
    quote:
      "The Hiring Leverage Score from HR AI saved us from making a premature hire. We waited 2 months and the ROI on that decision was huge.",
    author: "Michael Rodriguez",
    title: "Founder, Rodriguez Law Group",
  },
];

const outcomes = [
  { label: "Reduced bottleneck resolution time", value: "42%" },
  { label: "Improved profit margins", value: "27%" },
  { label: "Faster client onboarding", value: "35%" },
];

const useCases = [
  {
    title: "Mismatch in Delegation Patterns",
    description:
      "Ops AI detected that senior associates were spending 30% of their time on admin tasks — suggesting targeted training for junior staff.",
    icon: <SettingsRounded sx={{ fontSize: 20 }} />,
    accent: "#6247cc",
  },
  {
    title: "Revenue Pressure Rising",
    description:
      "Finance AI flagged declining margins on flat-fee cases — signalling tightening controls before hiring.",
    icon: <AttachMoneyRounded sx={{ fontSize: 20 }} />,
    accent: "#472ed1",
  },
  {
    title: "Case Intake Bottleneck",
    description:
      "Sales AI spotted a 20% drop in conversion after initial consultation — highlighting intake conversion decline.",
    icon: <TrendingUpRounded sx={{ fontSize: 20 }} />,
    accent: "#8f6ef0",
  },
];

const faqs = [
  {
    question: "How secure is the AI analysis?",
    answer:
      "All data is encrypted at rest and in transit. LexMom is SOC2 compliant and never shares your firm's data with third parties.",
  },
  {
    question: "What data sources does the AI use?",
    answer:
      "The AI ingests data from your practice management software, billing systems, CRM, document management, and other tools you authorize.",
  },
  {
    question: "How does AI make recommendations?",
    answer:
      "Our AI models are trained on thousands of law firm data points to identify patterns. Recommendations are generated based on your specific metrics compared against industry best practices.",
  },
  {
    question: "Where do I see AI outputs in the platform?",
    answer:
      "AI insights appear directly in your LexMom dashboard as actionable alerts, next-step cards, and trend visualizations. You can also receive weekly email summaries.",
  },
];

// ─── SVG WAVE DIVIDERS ─────────────────────────────────────────────────────────

const WaveUp = ({ fill, bg, rotateX = 0, rotateY = 0 }) => {
  const id = React.useId();

  const isGradient = bg?.from && bg?.to;

  return (
    <Box
      sx={{
        lineHeight: 0,
        mb: "-2px",
        transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
      }}
    >
      <svg
        viewBox="0 0 1440 72"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", width: "100%" }}
      >
        {isGradient && (
          <defs>
            <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={bg.from} />
              <stop offset="100%" stopColor={bg.to} />
            </linearGradient>
          </defs>
        )}

        <path
          d="M0,56 C360,0 1080,72 1440,16 L1440,0 L0,0 Z"
          fill={isGradient ? `url(#${id})` : fill || "#fff"}
        />
      </svg>
    </Box>
  );
};

// ─── SHARED STYLES ─────────────────────────────────────────────────────────────
const sectionPad = { px: { xs: 3, md: 8, lg: 12 }, py: { xs: 6, md: 8 } };
const headingSx = (theme) => ({
  fontSize: { xs: "1.7rem", md: "2.2rem" },
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
});
const subSx = (theme) => ({
  fontSize: { xs: "1rem", md: "1.05rem" },
  color: theme.palette.text.secondary,
  lineHeight: 1.75,
});

// ─── STAT PILL ─────────────────────────────────────────────────────────────────
const StatItem = ({ value, label, theme }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
    <Typography
      sx={{
        fontSize: { xs: "1.6rem", md: "2.2rem" },
        fontWeight: 700,
        color: theme.palette.text.primary,
        lineHeight: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.8rem",
        color: theme.palette.text.secondary,
      }}
    >
      {label}
    </Typography>
  </Box>
);

// ─── AI CARD ──────────────────────────────────────────────────────────────────
const AiCard = ({ manager, onClick, theme }) => (
  <motion.div
    whileHover={{
      y: -6,
      boxShadow: `0 20px 48px ${alpha(manager.accent, 0.13)}`,
    }}
    transition={{ duration: 0.22 }}
  >
    <Box
      onClick={onClick}
      sx={{
        borderRadius: "16px",
        border: `1.5px solid ${theme.palette.divider || "#e3e0f7"}`,
        background: theme.palette.background.paper,
        overflow: "hidden",
        cursor: "pointer",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "border-color 0.2s",
        "&:hover": { borderColor: manager.accent },
      }}
    >
      {/* Top stripe */}
      <Box
        sx={{
          height: 6,
          background: `linear-gradient(90deg, ${manager.accent}, ${alpha(manager.accent, 0.6)})`,
        }}
      />
      <Box
        sx={{
          p: 2.5,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              background: alpha(manager.accent, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: manager.accent,
            }}
          >
            {manager.icon}
          </Box>
          <Box>
            <Box
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: theme.palette.text.primary,
              }}
            >
              {manager.name}
            </Box>
            <Box
              sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}
            >
              {manager.title}
            </Box>
          </Box>
        </Box>

        {/* Description */}
        <Box
          sx={{
            fontSize: "0.82rem",
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            flex: 1,
          }}
        >
          {manager.description}
        </Box>

        {/* Efficiency bar */}
        <Box>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Box
              sx={{
                fontSize: "0.75rem",
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Efficiency
            </Box>
            <Box
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: manager.accent,
              }}
            >
              {manager.efficiency}%
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={manager.efficiency}
            sx={{
              height: 5,
              borderRadius: 3,
              background: alpha(manager.accent, 0.09),
              "& .MuiLinearProgress-bar": {
                background: manager.accent,
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {/* Features */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
          {manager.features.map((f, i) => (
            <Box
              key={i}
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                color: manager.accent,
                background: alpha(manager.accent, 0.07),
                px: 1,
                py: 0.3,
                borderRadius: "20px",
              }}
            >
              {f}
            </Box>
          ))}
        </Box>

        {/* Value */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: "8px",
            background: alpha(manager.accent, 0.04),
            border: `1px solid ${alpha(manager.accent, 0.12)}`,
          }}
        >
          <Box
            sx={{ fontSize: "0.8rem", fontWeight: 600, color: manager.accent }}
          >
            {manager.value}
          </Box>
        </Box>
      </Box>
    </Box>
  </motion.div>
);

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
const AiTeamPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedAI, setSelectedAI] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  const handleCardClick = (manager) => {
    setSelectedAI(manager);
    document.body.style.overflow = "hidden";
  };
  const handleClose = () => {
    setSelectedAI(null);
    document.body.style.overflow = "unset";
  };

  // Colors derived from theme
  const primaryMain = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;
  const backgroundPaper = theme.palette.background.paper;
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const divider = theme.palette.divider || "#e3e0f7";

  // Helper for alpha colors
  const alphaPrimary = (opacity) => alpha(primaryMain, opacity);
  const alphaWhite = (opacity) => `rgba(255,255,255,${opacity})`;

  return (
    <Box
      sx={{
        postion: "relative",
        color: textPrimary,
        overflowX: "hidden",
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          ...sectionPad,
          pt: { xs: 8, md: 10 },
          pb: { xs: 2, md: 4 },
          textAlign: "center",
        }}
      >
        {" "}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              background: alphaPrimary(0.06),
              border: `1px solid ${alphaPrimary(0.2)}`,
              borderRadius: "100px",
              px: 2,
              py: 0.6,
              mb: 3,
            }}
          >
            <BoltRounded sx={{ fontSize: 14, color: primaryMain }} />
            <Box
              sx={{ fontSize: "0.78rem", fontWeight: 600, color: primaryMain }}
            >
              8 Specialized AI Managers · Coming Soon
            </Box>
          </Box>

          <Typography
            component={motion.h1}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{
              fontWeight: 900,
              fontFamily: "'Playfair Display', Georgia, serif",
              color: "text.primary",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              textAlign: "center",
              fontSize: {
                xs: "1.7rem",
                sm: "2.2rem",
                md: "2.8rem",
                lg: "3.2rem",
              },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: { xs: "0.2em", md: "0.3em" },
            }}
          >
            AI-Powered Growth Intelligence
            <br />
            <Box component="span" sx={{ color: primaryMain }}>
              for Law Firms
            </Box>
          </Typography>

          <Box sx={{ ...subSx(theme), maxWidth: 580, mx: "auto", mb: 4 }}>
            LexMom's family of AI Managers continuously analyzes your firm's
            data — identifying bottlenecks, predicting structural risks, and
            guiding your next best moves.
          </Box>

          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.25,
              justifyContent: "center",
              width: "100%",
              maxWidth: { xs: "280px", sm: "none" },
            }}
          >
            <Button
              variant="contained"
              endIcon={<ArrowForwardRounded />}
              onClick={() => navigate("/start-trial")}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.9rem", md: "0.95rem" },
                px: { xs: 2.5, md: 3 },
                py: 1,
                background: `linear-gradient(135deg, ${primaryMain} 0%, ${primaryDark} 100%)`,
                boxShadow: `0 4px 16px ${alpha(primaryMain, 0.35)}`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${alpha(primaryMain, 0.9)} 0%, ${primaryDark} 100%)`,
                  boxShadow: `0 6px 24px ${alpha(primaryMain, 0.45)}`,
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              See AI in Action
            </Button>
            <Button
              variant="outlined"
              startIcon={<PlayArrowRounded />}
              onClick={() => navigate("/founding-50")}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: { xs: "0.9rem", md: "0.95rem" },
                px: { xs: 2.5, md: 3 },
                py: 1,
                color: primaryMain,
                borderColor: alpha(primaryMain, 0.35),
                "&:hover": {
                  borderColor: alpha(primaryMain, 0.65),
                  background: alpha(primaryMain, 0.05),
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Watch Overview
            </Button>
          </Box>
        </motion.div>
      </Box>

      {/* Stats Section */}
      <Box sx={{ ...sectionPad, pt: 0 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              background: backgroundPaper,
              borderRadius: "28px",
              px: { xs: 3, md: 6 },
              py: { xs: 4, md: 5 },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 4, md: 0 },
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            }}
          >
            <Box sx={{ maxWidth: 420 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.4rem", md: "1.8rem" },
                  fontWeight: 700,
                  color: textPrimary,
                  mb: 1,
                }}
              >
                Let’s build something great.
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: textSecondary,
                  lineHeight: 1.6,
                }}
              >
                Enim sed faucibus turpis in eu mi bibendum neque egestas. Mi
                bibendum neque egestas congue quisque egestas diam in arcu. Elit
                pellentesque habitant.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, auto)",
                gap: { xs: 3, md: 5 },
                alignItems: "center",
              }}
            >
              <StatItem
                value="8"
                label="Specialized AI Managers"
                theme={theme}
              />
              <StatItem
                value="90%"
                label="Avg Efficiency Score"
                theme={theme}
              />
              <StatItem value="35%" label="Avg Conversion Lift" theme={theme} />
              <StatItem
                value="24/7"
                label="Continuous Monitoring"
                theme={theme}
              />
            </Box>
          </Box>
        </motion.div>
      </Box>

      <WaveUp fill="#fff" rotateX={180} rotateY={180} />
      {/* Why AI Matters Section */}
      <Box sx={{ background: "#fff", ...sectionPad }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Box sx={{ ...headingSx(theme), mb: 1.5 }}>
              Data Without Direction Keeps Firms Stuck
            </Box>
            <Box sx={{ ...subSx(theme), maxWidth: 680, mx: "auto" }}>
              Today's law firms collect mountains of data — from cases,
              finances, hiring metrics, and workflows. But without structured
              intelligence, that data rarely translates into growth.
            </Box>
          </Box>

          {/* Bento Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
                lg: "2fr 1fr 1fr",
              },
              gridTemplateRows: { lg: "auto auto" },
              gap: 2,
            }}
          >
            {/* Big card */}
            <Box
              sx={{
                gridRow: { lg: "1 / 3" },
                background: `linear-gradient(140deg, ${primaryMain} 0%, ${primaryDark} 100%)`,
                borderRadius: "20px",
                p: 4,
                color: "#fff",
                display: "flex",
                flexDirection: "column",
                gap: 3,
                minHeight: { md: 320 },
              }}
            >
              <PsychologyRounded sx={{ fontSize: 48, opacity: 0.9 }} />
              <Box
                sx={{ fontSize: "1.4rem", fontWeight: 700, lineHeight: 1.3 }}
              >
                LexMom's AI doesn't just report numbers.
                <br />
                It tells you what to fix next.
              </Box>
              <Box
                sx={{
                  fontSize: "0.9rem",
                  opacity: 0.85,
                  lineHeight: 1.7,
                  flex: 1,
                }}
              >
                Our AI layer interprets your firm's data, prioritizes what
                matters, and delivers clear, actionable recommendations across 8
                critical domains.
              </Box>
              <Button
                variant="contained"
                onClick={() => navigate("/start-trial")}
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  px: 3,
                  py: 1,
                  background: "#fff",
                  color: primaryMain,
                  boxShadow: "none",
                  "&:hover": {
                    background: alpha("#fff", 0.95),
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Join Waitlist →
              </Button>
            </Box>

            {/* 4 smaller bento cells */}
            {[
              {
                icon: (
                  <CheckCircleRounded
                    sx={{ color: primaryMain, fontSize: 22 }}
                  />
                ),
                title: "Stop guessing where the bottleneck is",
                desc: "AI pinpoints exactly where your workflow breaks down.",
              },
              {
                icon: (
                  <PeopleRounded sx={{ color: primaryMain, fontSize: 22 }} />
                ),
                title: "Know when you're ready to hire",
                desc: "Hire only when it creates leverage, not overhead.",
              },
              {
                icon: (
                  <AnalyticsRounded sx={{ color: primaryMain, fontSize: 22 }} />
                ),
                title: "Forecast structural limitations",
                desc: "See risks before they become crises.",
              },
              {
                icon: (
                  <LightbulbRounded sx={{ color: primaryMain, fontSize: 22 }} />
                ),
                title: "Actionable next steps—not dashboards",
                desc: "Specific guidance, not just charts and numbers.",
              },
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  background: backgroundPaper,
                  border: `1.5px solid ${divider}`,
                  borderRadius: "16px",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: alphaPrimary(0.06),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </Box>
                <Box
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.88rem",
                    color: textPrimary,
                  }}
                >
                  {item.title}
                </Box>
                <Box
                  sx={{
                    fontSize: "0.8rem",
                    color: textSecondary,
                    lineHeight: 1.6,
                  }}
                >
                  {item.desc}
                </Box>
              </Box>
            ))}
          </Box>
        </motion.div>
      </Box>
      <WaveUp fill="#fff" rotateX={0} rotateY={0} />

      {/* AI Managers Grid */}
      <Box sx={{ ...sectionPad }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Box sx={{ ...headingSx(theme), mb: 1.5 }}>
              Meet Your AI Executive Team
            </Box>
            <Box sx={{ ...subSx(theme), maxWidth: 580, mx: "auto" }}>
              Each AI Manager is specialized to monitor and optimize a core
              growth domain — like a human leadership function, but smarter and
              continuous.
            </Box>
          </Box>
        </motion.div>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: "repeat(4, 1fr)",
            },
            gap: 2.5,
          }}
        >
          {aiManagers.map((manager, i) => (
            <motion.div
              key={manager.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
            >
              <AiCard
                manager={manager}
                onClick={() => handleCardClick(manager)}
                theme={theme}
              />
            </motion.div>
          ))}
        </Box>
      </Box>

      {/* How It Works (wave section) */}
      <WaveUp
        bg={{ from: primaryMain, to: primaryDark }}
        rotateX={180}
        rotateY={0}
      />
      <Box
        sx={{
          background: `linear-gradient(135deg, ${primaryMain} 0%, ${primaryDark} 100%)`,
          position: "relative",
        }}
      >
        <Box sx={{ ...sectionPad, color: "#fff" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Box
                sx={{
                  fontSize: { xs: "1.7rem", md: "2.2rem" },
                  fontWeight: 700,
                  color: "#fff",
                  mb: 1.5,
                  lineHeight: 1.2,
                }}
              >
                How LexMom AI Works Inside Your Firm
              </Box>
              <Box
                sx={{
                  fontSize: "1rem",
                  color: alphaWhite(0.75),
                  maxWidth: 580,
                  mx: "auto",
                }}
              >
                From raw data to clear next steps — here's the intelligence
                pipeline powering your firm.
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 3,
                position: "relative",
              }}
            >
              {[
                {
                  icon: <StorageRounded sx={{ fontSize: 36, color: "#fff" }} />,
                  step: "01",
                  title: "Data Collection",
                  desc: "Every part of your firm workflow feeds into a unified dataset — cases, billing, workflow logs, and activity signals.",
                },
                {
                  icon: (
                    <AnalyticsRounded sx={{ fontSize: 36, color: "#fff" }} />
                  ),
                  step: "02",
                  title: "Pattern Detection",
                  desc: "AI spotlights trends, bottlenecks, and stage misalignments across all 8 operational domains in real time.",
                },
                {
                  icon: (
                    <LightbulbRounded sx={{ fontSize: 36, color: "#fff" }} />
                  ),
                  step: "03",
                  title: "Action Guidance",
                  desc: "Instead of dashboards with numbers, you receive growth opportunities, structural risk alerts, and next step recommendations.",
                },
              ].map((s, i) => (
                <Box
                  key={i}
                  sx={{
                    background: alphaWhite(0.1),
                    border: `1px solid ${alphaWhite(0.2)}`,
                    borderRadius: "20px",
                    p: 4,
                    backdropFilter: "blur(8px)",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: "3.5rem",
                      fontWeight: 800,
                      color: alphaWhite(0.1),
                      position: "absolute",
                      top: 16,
                      right: 20,
                      lineHeight: 1,
                    }}
                  >
                    {s.step}
                  </Box>
                  <Box sx={{ mb: 2 }}>{s.icon}</Box>
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "#fff",
                      mb: 1,
                    }}
                  >
                    {s.title}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "0.85rem",
                      color: alphaWhite(0.75),
                      lineHeight: 1.7,
                    }}
                  >
                    {s.desc}
                  </Box>
                </Box>
              ))}
            </Box>
          </motion.div>
        </Box>
      </Box>
      <WaveUp
        bg={{ from: primaryDark, to: primaryMain }}
        rotateX={0}
        rotateY={-180}
      />

      {/* Use Cases Section */}
      <Box sx={{ ...sectionPad }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Box sx={{ ...headingSx(theme), mb: 1.5 }}>
              AI Insights With Real Business Value
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 2.5,
            }}
          >
            {useCases.map((uc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Box
                  sx={{
                    border: `1.5px solid ${divider}`,
                    borderRadius: "16px",
                    p: 3.5,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    background: backgroundPaper,
                    "&:hover": {
                      borderColor: uc.accent,
                      boxShadow: `0 8px 32px ${alpha(uc.accent, 0.09)}`,
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      background: alpha(uc.accent, 0.08),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: uc.accent,
                    }}
                  >
                    {uc.icon}
                  </Box>
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: textPrimary,
                    }}
                  >
                    {uc.title}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "0.83rem",
                      color: textSecondary,
                      lineHeight: 1.65,
                    }}
                  >
                    {uc.description}
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Box>

      {/* Comparison Section */}
      <Box sx={{ background: alphaPrimary(0.05), ...sectionPad }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Box sx={{ ...headingSx(theme), mb: 1.5 }}>
              AI That Guides — Not Just Reports
            </Box>
          </Box>
          <Box
            sx={{
              maxWidth: 700,
              mx: "auto",
              border: `1.5px solid ${divider}`,
              borderRadius: "20px",
              overflow: "hidden",
              background: backgroundPaper,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr",
                background: primaryMain,
                px: 3,
                py: 2,
              }}
            >
              <Box
                sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#fff" }}
              >
                Feature
              </Box>
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  color: "#fff",
                  textAlign: "center",
                }}
              >
                LexMom AI
              </Box>
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  color: alphaWhite(0.6),
                  textAlign: "center",
                }}
              >
                Traditional Tools
              </Box>
            </Box>
            {[
              "Predictive Growth Guidance",
              "Structural Bottleneck Alerts",
              "Hiring Leverage Score",
              "Actionable Next Steps",
              "Real-Time Risk Monitoring",
            ].map((f, i) => (
              <Box
                key={i}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  px: 3,
                  py: 1.75,
                  borderBottom: i < 4 ? `1px solid ${divider}` : "none",
                  background:
                    i % 2 === 0 ? backgroundPaper : alphaPrimary(0.05),
                }}
              >
                <Box
                  sx={{
                    fontSize: "0.875rem",
                    color: textPrimary,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {f}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CheckCircleRounded
                    sx={{ color: primaryMain, fontSize: 22 }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CloseRounded sx={{ color: "#d0cce8", fontSize: 22 }} />
                </Box>
              </Box>
            ))}
          </Box>
        </motion.div>
      </Box>

      {/* Social Proof Section */}
      <Box sx={{ ...sectionPad }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Box sx={{ ...headingSx(theme), mb: 1.5 }}>
              Firms Powered by AI-Driven Growth
            </Box>
          </Box>

          {/* Outcome stats bento */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 2.5,
              mb: 4,
            }}
          >
            {outcomes.map((o, i) => (
              <Box
                key={i}
                sx={{
                  background:
                    i === 1
                      ? `linear-gradient(135deg, ${primaryMain}, ${primaryDark})`
                      : backgroundPaper,
                  border: `1.5px solid ${i === 1 ? primaryMain : divider}`,
                  borderRadius: "20px",
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    fontSize: "3rem",
                    fontWeight: 800,
                    color: i === 1 ? "#fff" : primaryMain,
                    lineHeight: 1,
                    mb: 1,
                  }}
                >
                  {o.value}
                </Box>
                <Box
                  sx={{
                    fontSize: "0.85rem",
                    color: i === 1 ? alphaWhite(0.8) : textSecondary,
                  }}
                >
                  {o.label}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Testimonials */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2.5,
            }}
          >
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Box
                  sx={{
                    border: `1.5px solid ${divider}`,
                    borderRadius: "20px",
                    p: 4,
                    background: backgroundPaper,
                    position: "relative",
                  }}
                >
                  <FormatQuoteRounded
                    sx={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      fontSize: 40,
                      color: alphaPrimary(0.1),
                    }}
                  />
                  <Box
                    sx={{
                      fontSize: "0.9rem",
                      color: textPrimary,
                      lineHeight: 1.75,
                      fontStyle: "italic",
                      mb: 3,
                    }}
                  >
                    "{t.quote}"
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: primaryMain,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                      }}
                    >
                      {t.author
                        .split(" ")
                        .map((w) => w[0])
                        .join("")}
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: textPrimary,
                        }}
                      >
                        {t.author}
                      </Box>
                      <Box sx={{ fontSize: "0.75rem", color: textSecondary }}>
                        {t.title}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ ...sectionPad }}>
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Box
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.15em",
              color: primaryMain,
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            [ F R E Q U E N T L Y A S K E D ]
          </Box>
          <Box sx={{ ...headingSx(theme), mb: 1 }}>
            Questions About AI Intelligence
          </Box>
          <Box sx={{ ...subSx(theme) }}>
            Get answers about how our AI works, security, and integration.
          </Box>
        </Box>
        <Box
          sx={{
            maxWidth: 700,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
            >
              <Box
                sx={{
                  border: `1.5px solid ${openFaq === i ? primaryMain : divider}`,
                  borderRadius: "14px",
                  overflow: "hidden",
                  background: backgroundPaper,
                  transition: "border-color 0.2s",
                }}
              >
                <Box
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 3,
                    py: 2.5,
                    cursor: "pointer",
                    "&:hover": { background: alphaPrimary(0.03) },
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      color: textPrimary,
                      pr: 2,
                    }}
                  >
                    {faq.question}
                  </Box>
                  <KeyboardArrowDownRounded
                    sx={{
                      color: primaryMain,
                      fontSize: 22,
                      transform: openFaq === i ? "rotate(180deg)" : "rotate(0)",
                      transition: "transform 0.25s",
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      style={{ overflow: "hidden" }}
                    >
                      <Box
                        sx={{
                          px: 3,
                          pb: 3,
                          fontSize: "0.85rem",
                          color: textSecondary,
                          lineHeight: 1.7,
                          borderTop: `1px solid ${divider}`,
                          pt: 2,
                        }}
                      >
                        {faq.answer}
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Box>

      {/* Final CTA Section */}
      <Box sx={{ ...sectionPad, background: alphaPrimary(0.05) }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${primaryMain} 0%, ${primaryDark} 100%)`,
              borderRadius: "28px",
              p: { xs: 4, md: 7 },
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative circles */}
            <Box
              sx={{
                position: "absolute",
                width: 300,
                height: 300,
                borderRadius: "50%",
                background: alphaWhite(0.05),
                top: -100,
                right: -60,
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: alphaWhite(0.06),
                bottom: -80,
                left: 40,
                pointerEvents: "none",
              }}
            />

            <PsychologyRounded
              sx={{ fontSize: 52, color: alphaWhite(0.9), mb: 2.5 }}
            />
            <Box
              sx={{
                fontSize: { xs: "1.7rem", md: "2.4rem" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
                lineHeight: 1.2,
              }}
            >
              See Your AI-Driven
              <br />
              Growth Intelligence
            </Box>
            <Box
              sx={{
                fontSize: "1rem",
                color: alphaWhite(0.75),
                mb: 4,
                maxWidth: 480,
                mx: "auto",
              }}
            >
              Limited spots available for early adopters. Get access to all 8 AI
              Managers before launch.
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                onClick={() => navigate("/start-trial")}
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  px: 3,
                  py: 1,
                  background: "#fff",
                  color: primaryMain,
                  boxShadow: "none",
                  "&:hover": {
                    background: alpha("#fff", 0.95),
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  },
                  transition: "all 0.2s ease",
                }}
                startIcon={<RocketLaunchRounded sx={{ fontSize: 18 }} />}
              >
                Run AI Growth Assessment
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/contact")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  border: "1.5px solid rgba(255,255,255,0.35)",
                  borderRadius: "100px",
                  padding: "8px 16px",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Book a Live Demo
              </Button>
            </Box>
          </Box>
        </motion.div>
      </Box>

      <AnimatePresence>
        {selectedAI && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(20,16,50,0.55)",
              backdropFilter: "blur(10px)",
              zIndex: 9999,
            }}
            onClick={handleClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 40 }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              style={{
                width: "min(92vw, 680px)",
                maxWidth: "92vw",
                borderRadius: 20,
                overflow: "hidden",
                boxShadow: `0 32px 80px ${alpha(selectedAI.accent, 0.25)}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{
                  maxHeight: "88vh",
                  overflowY: "auto",
                  background: backgroundPaper,
                }}
              >
                {/* Modal header */}
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${selectedAI.accent} 0%, ${primaryMain} 100%)`,
                    p: 3.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        background: alphaWhite(0.18),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                      }}
                    >
                      {selectedAI.icon}
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.3rem",
                          color: "#fff",
                        }}
                      >
                        {selectedAI.name}
                      </Box>
                      <Box
                        sx={{ fontSize: "0.82rem", color: alphaWhite(0.78) }}
                      >
                        {selectedAI.title}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    onClick={handleClose}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: alphaWhite(0.15),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#fff",
                      "&:hover": { background: alphaWhite(0.25) },
                    }}
                  >
                    <CloseRounded sx={{ fontSize: 20 }} />
                  </Box>
                </Box>

                {/* Modal body (same as before) */}
                <Box
                  sx={{
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <Box
                    sx={{
                      fontSize: "0.95rem",
                      color: textSecondary,
                      lineHeight: 1.8,
                    }}
                  >
                    {selectedAI.detailedDescription}
                  </Box>

                  <Box>
                    <Box
                      sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: textPrimary,
                        mb: 2,
                      }}
                    >
                      Key Features
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedAI.features.map((f, i) => (
                        <Box
                          key={i}
                          sx={{
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: selectedAI.accent,
                            background: alpha(selectedAI.accent, 0.07),
                            border: `1px solid ${alpha(selectedAI.accent, 0.15)}`,
                            px: 2,
                            py: 0.7,
                            borderRadius: "100px",
                          }}
                        >
                          {f}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 3,
                      borderRadius: "14px",
                      background: alpha(selectedAI.accent, 0.05),
                      border: `1.5px solid ${alpha(selectedAI.accent, 0.12)}`,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: selectedAI.accent,
                        mb: 0.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      Value Proposition
                    </Box>
                    <Box
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        color: textPrimary,
                      }}
                    >
                      {selectedAI.value}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, pt: 1 }}>
                    <Button
                      onClick={() => {
                        handleClose();
                        navigate("/start-trial");
                      }}
                      sx={{
                        flex: 1,
                        borderRadius: "999px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        px: 3,
                        py: 1.15,
                        background: selectedAI.accent,
                        color: "#fff",
                        "&:hover": {
                          background: alpha(selectedAI.accent, 0.9),
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      Get Early Access →
                    </Button>
                    <Button
                      onClick={handleClose}
                      sx={{
                        flex: 1,
                        borderRadius: "999px",
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.9rem",
                        px: 3,
                        py: 1.15,
                        background: alpha(primaryMain, 0.08),
                        color: textSecondary,
                        "&:hover": {
                          background: alpha(primaryMain, 0.12),
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default AiTeamPage;
